import { Form, Formik, FormikProps } from 'formik';
import { Button, Stack, TextField, Box, Typography } from '@mui/material';
import { Field } from 'formik';
import FormTextInput from './FormTextInput';
import { ItemCategory } from '../schema';
import * as Yup from 'yup';

interface CategoryFormValues {
    name: string;
    description: string;
    color: string;
}

interface Props {
    category?: ItemCategory;
    onSubmit: (values: CategoryFormValues) => Promise<void>;
    onCancel: () => void;
    isSubmitting?: boolean;
}

const categoryFormSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    color: Yup.string().matches(/^#[0-9A-F]{6}$/i, 'Must be a valid hex color (e.g., #FF5733)'),
});

export default function CategoryForm({ category, onSubmit, onCancel, isSubmitting = false }: Props) {
    const initialValues: CategoryFormValues = {
        name: category?.name || '',
        description: category?.description || '',
        color: category?.color || '#2196F3',
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={categoryFormSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    await onSubmit(values);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ values, isSubmitting: formikSubmitting }: FormikProps<CategoryFormValues>) => (
                <Form>
                    <Stack spacing={2}>
                        <Field name="name" component={FormTextInput} label="Name" required fullWidth />
                        <Field name="description" component={FormTextInput} label="Description" fullWidth multiline rows={3} />

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Color
                            </Typography>
                            <Field name="color">
                                {({ field, form }: any) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <TextField
                                            {...field}
                                            type="color"
                                            error={form.touched.color && Boolean(form.errors.color)}
                                            helperText={form.touched.color && form.errors.color}
                                            sx={{ width: 100 }}
                                        />
                                        <TextField
                                            {...field}
                                            label="Hex Color"
                                            placeholder="#2196F3"
                                            error={form.touched.color && Boolean(form.errors.color)}
                                            helperText={form.touched.color && form.errors.color}
                                            sx={{ flex: 1 }}
                                        />
                                        {values.color && (
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1,
                                                    backgroundColor: values.color,
                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Field>
                        </Box>

                        <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                            <Button onClick={onCancel} disabled={formikSubmitting || isSubmitting}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={formikSubmitting || isSubmitting}>
                                {category ? 'Update' : 'Create'}
                            </Button>
                        </Stack>
                    </Stack>
                </Form>
            )}
        </Formik>
    );
}
